import React, { useState } from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { ScrollArea } from "../../../components/ui/scroll-area";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Avatar } from "../../../components/ui/avatar";
import { InventoryHistoryEntry, FieldChange, InventoryItem } from "../types/inventory";
import { useItemHistory } from "../hooks/useItemHistory";

interface ViewHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: InventoryItem | null;
}

// Helper to format field values for display
const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (value instanceof Date) return format(value, "PPP");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

// Component to show before/after values
const FieldDiff: React.FC<{ change: FieldChange }> = ({ change }) => {
  const displayName = change.displayName || change.field;
  
  return (
    <div className="flex flex-col space-y-1 text-sm">
      <div className="font-medium">{displayName}</div>
      <div className="flex items-center space-x-2">
        <div className="flex-1 bg-muted/50 p-2 rounded-md line-through text-muted-foreground">
          {formatFieldValue(change.oldValue)}
        </div>
        <div className="flex-none">→</div>
        <div className="flex-1 bg-muted p-2 rounded-md font-medium">
          {formatFieldValue(change.newValue)}
        </div>
      </div>
    </div>
  );
};

// Timeline entry component
const HistoryEntry: React.FC<{ entry: InventoryHistoryEntry }> = ({ entry }) => {
  const timestamp = entry.timestamp instanceof Date 
    ? entry.timestamp 
    : new Date(entry.timestamp);

  return (
    <div className="relative pl-8 pb-8 last:pb-0">
      {/* Timeline connector */}
      <div className="absolute left-[11px] top-2 -bottom-2 w-px bg-border last:hidden" />
      
      {/* Timeline dot */}
      <div className="absolute left-0 top-2 w-6 h-6 rounded-full border-2 border-primary bg-background flex items-center justify-center">
        <div className="w-2 h-2 rounded-full bg-primary" />
      </div>

      <div className="space-y-2">
        {/* Header with user and timestamp */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Avatar className="h-6 w-6">
              <span className="text-xs">{entry.userName.slice(0, 2)}</span>
            </Avatar>
            <span className="text-sm font-medium">{entry.userName}</span>
          </div>
          <time className="text-sm text-muted-foreground">
            {format(timestamp, "PPP 'at' h:mm a")}
          </time>
        </div>

        {/* Changes */}
        <div className="space-y-4 pt-2">
          {entry.changes.map((change, idx) => (
            <FieldDiff key={idx} change={change} />
          ))}
        </div>

        {/* Reason if provided */}
        {entry.reason && (
          <div className="pt-2">
            <Badge variant="outline">{entry.reason}</Badge>
          </div>
        )}
      </div>
    </div>
  );
};

export function ViewHistoryDialog({
  open,
  onOpenChange,
  item,
}: ViewHistoryDialogProps) {
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data, isLoading } = useItemHistory({
    itemId: item?.id,
    page,
    pageSize,
  });

  // Reset page when item changes
  React.useEffect(() => {
    setPage(1);
  }, [item?.id]);

  const totalPages = data?.total ? Math.ceil(data.total / pageSize) : 0;

  // Sort entries by timestamp
  const sortedEntries = React.useMemo(() => {
    if (!data?.entries?.length) return [];

    return [...data.entries].sort((a, b) => {
      const timestampA = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp);
      const timestampB = b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp);
      return timestampB.getTime() - timestampA.getTime();
    });
  }, [data?.entries]);

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>History - {item.name}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[600px] pr-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : !sortedEntries.length ? (
            <div className="text-center py-8 text-muted-foreground">
              No history available
            </div>
          ) : (
            <div className="space-y-6">
              {sortedEntries.map((entry) => (
                <HistoryEntry key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </ScrollArea>

        {totalPages > 1 && (
          <div className="flex justify-center pt-4">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => !isLoading && page > 1 && setPage(page - 1)}
                disabled={isLoading || page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-[4rem] text-center">
                {page} / {totalPages}
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => !isLoading && page < totalPages && setPage(page + 1)}
                disabled={isLoading || page === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
